import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { SignIn } from '../auth/SignIn'

/**
 * In-page login slide-over for the public find-parking screen (slice 5).
 *
 * Wraps the EXISTING SignIn flow (phone-first SMS code, email magic-link
 * fallback, Turnstile) in a right-hand panel so a logged-out visitor can sign
 * in without ever navigating away from the browse experience. No new auth —
 * this is purely presentation over auth/SignIn.tsx.
 */
export function LoginSlideOver({
  open, onClose, onSignedIn,
}: {
  open: boolean
  onClose: () => void
  onSignedIn: () => void
}) {
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  // Portal to <body>: the panel is position:fixed, and any transformed
  // ancestor (e.g. the screen's .fadein entrance animation) would otherwise
  // become its containing block and mis-anchor the slide-in.
  return createPortal(
    <div
      className={`find-login${open ? ' find-login--open' : ''}`}
      aria-hidden={!open}
    >
      <div className="find-login__scrim" onClick={onClose} />
      <div
        className="find-login__panel"
        role="dialog"
        aria-modal="true"
        aria-label="Log in"
      >
        <div className="find-login__head">
          <span style={{ fontSize: 17, fontWeight: 800, color: 'var(--color-black)' }}>Log in</span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{ padding: 6, lineHeight: 0 }}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--color-black)"
              strokeWidth="2.4" strokeLinecap="round" aria-hidden>
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <p style={{ margin: '0 0 14px', fontSize: 13, color: 'var(--color-gray-700)', lineHeight: 1.5 }}>
          You'll stay right here on account.parkify.com.
        </p>
        {/* Remount on open so a reopened panel starts back at the phone step. */}
        {open && <SignIn onSignedIn={onSignedIn} />}
      </div>
    </div>,
    document.body,
  )
}
